import React, { useEffect, useState } from 'react';
import { hrmsListInterviews, hrmsUpdateInterview, hrmsCancelInterview, hrmsSummarizeInterview } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { CalendarClock, Check, X, MessageSquare, Video, MapPin, Phone, Calendar, Sparkles, Loader2 } from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-500', completed: 'bg-emerald-500', cancelled: 'bg-slate-500', rescheduled: 'bg-amber-500',
};

const ModeIcon = ({ mode }) => {
  if (mode === 'Phone') return <Phone className="h-3.5 w-3.5" />;
  if (mode === 'In-person') return <MapPin className="h-3.5 w-3.5" />;
  return <Video className="h-3.5 w-3.5" />;
};

const InterviewsPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedback, setFeedback] = useState({ rating: 5, recommendation: 'yes', feedback: '' });
  const [rawNotes, setRawNotes] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  const load = async () => setRows(await hrmsListInterviews().catch(() => []));
  useEffect(() => { load(); }, []);

  const submitFeedback = async () => {
    try {
      await hrmsUpdateInterview(feedbackFor.id, { ...feedback, status: 'completed', rating: Number(feedback.rating) });
      toast({ title: 'Feedback saved' });
      setFeedbackFor(null); setFeedback({ rating: 5, recommendation: 'yes', feedback: '' });
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '' });
    }
  };

  const cancel = async (iv) => {
    if (!window.confirm(`Cancel interview ${iv.interview_id}?`)) return;
    await hrmsCancelInterview(iv.id);
    toast({ title: 'Interview cancelled' });
    load();
  };

  return (
    <div data-testid="interviews-page">
      <h1 className="text-2xl font-extrabold">Interviews</h1>
      <p className="text-sm text-slate-500 mt-1">Scheduled interviews across the pipeline. Capture feedback here.</p>

      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Interview ID</th>
            <th className="text-left px-4 py-3">Candidate</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Round</th>
            <th className="text-left px-4 py-3">Date &amp; time</th>
            <th className="text-left px-4 py-3">Mode</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No interviews scheduled yet. Open a candidate card from the Pipeline to schedule one.</td></tr>}
            {rows.map((iv) => (
              <tr key={iv.id} className="border-t align-top" data-testid={`interview-row-${iv.interview_id}`}>
                <td className="px-4 py-3 font-mono text-xs">{iv.interview_id}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{iv.candidate_name}<div className="text-xs text-slate-500">{iv.candidate_email}</div></td>
                <td className="px-4 py-3">{iv.job_title}</td>
                <td className="px-4 py-3">{iv.round_name}</td>
                <td className="px-4 py-3">{iv.date} · {iv.time_start}{iv.time_end ? `–${iv.time_end}` : ''} <span className="text-xs text-slate-500">({iv.timezone})</span></td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1"><ModeIcon mode={iv.mode} /> {iv.mode}</span>
                  {iv.meet_link && <div className="mt-1"><a href={iv.meet_link} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1" data-testid={`interview-meet-${iv.interview_id}`}><Video className="h-3 w-3" /> Google Meet</a></div>}
                </td>
                <td className="px-4 py-3"><Badge className={STATUS_COLORS[iv.status] || 'bg-slate-500'}>{iv.status}</Badge></td>
                <td className="px-4 py-3 space-x-1">
                  {iv.gcal_url && (
                    <a href={iv.gcal_url} target="_blank" rel="noreferrer" className="inline-flex items-center h-8 px-2 text-xs rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50" title="Add to Google Calendar" data-testid={`interview-gcal-${iv.interview_id}`}>
                      <Calendar className="h-3.5 w-3.5 mr-1 text-blue-600" /> GCal
                    </a>
                  )}
                  {iv.status === 'scheduled' && (
                    <>
                      <Button variant="outline" onClick={() => { setFeedbackFor(iv); setFeedback({ rating: 5, recommendation: 'yes', feedback: '' }); }} className="h-8 px-2" data-testid={`interview-feedback-${iv.interview_id}`}><MessageSquare className="h-3.5 w-3.5 mr-1" /> Feedback</Button>
                      <Button variant="outline" onClick={() => cancel(iv)} className="h-8 px-2 text-red-600 border-red-200" data-testid={`interview-cancel-${iv.interview_id}`}><X className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                  {iv.status === 'completed' && iv.feedback && (
                    <div className="text-[11px] text-slate-600 max-w-xs whitespace-normal">
                      <div className="font-semibold">★ {iv.rating}/5 · {iv.recommendation}</div>
                      <div className="text-slate-500 line-clamp-2">{iv.feedback}</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!feedbackFor} onOpenChange={(v) => { if (!v) { setFeedbackFor(null); setRawNotes(''); setAiSummary(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogTitle>Interview feedback · {feedbackFor?.candidate_name}</DialogTitle>
          {feedbackFor && (
            <div className="mt-2 space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {/* Raw notes + AI summarize */}
              <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1 text-purple-800"><Sparkles className="h-3.5 w-3.5" /> Paste raw notes → AI will fill the form below</Label>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!rawNotes.trim()) { toast({ title: 'Paste some notes first' }); return; }
                      setAiBusy(true);
                      try {
                        const s = await hrmsSummarizeInterview(feedbackFor.id, rawNotes);
                        setAiSummary(s);
                        // Auto-fill the feedback form
                        const bullet = (arr, label) => (arr || []).length ? `${label}:\n${(arr).map((x) => `• ${x}`).join('\n')}` : '';
                        const combined = [s.summary, bullet(s.strengths, 'Strengths'), bullet(s.concerns, 'Concerns')].filter(Boolean).join('\n\n');
                        setFeedback({ rating: s.rating, recommendation: s.recommendation, feedback: combined });
                        toast({ title: 'AI summarized', description: `${s.rating}/5 · ${s.recommendation}` });
                      } catch (err) { toast({ title: 'Summarize failed', description: err?.response?.data?.detail || '' }); }
                      finally { setAiBusy(false); }
                    }}
                    disabled={aiBusy}
                    className="h-8 bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="interview-ai-summarize"
                  >{aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Summarize with AI</>}</Button>
                </div>
                <Textarea rows={5} value={rawNotes} onChange={(e) => setRawNotes(e.target.value)} placeholder="Paste your raw interview notes here — messy is fine…" className="mt-2 font-mono text-xs" data-testid="interview-raw-notes" />
                {aiSummary && (
                  <div className="mt-2 text-xs text-purple-900 border-t border-purple-200 pt-2">
                    <b>AI verdict:</b> {aiSummary.summary}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rating (1–5)</Label>
                  <Input type="number" min={1} max={5} value={feedback.rating} onChange={(e) => setFeedback({ ...feedback, rating: e.target.value })} data-testid="interview-rating-input" />
                </div>
                <div>
                  <Label>Recommendation</Label>
                  <select value={feedback.recommendation} onChange={(e) => setFeedback({ ...feedback, recommendation: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm mt-1" data-testid="interview-recommendation-select">
                    <option value="strong_yes">Strong yes</option>
                    <option value="yes">Yes</option>
                    <option value="maybe">Maybe</option>
                    <option value="no">No</option>
                    <option value="strong_no">Strong no</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Detailed feedback</Label>
                <Textarea rows={7} value={feedback.feedback} onChange={(e) => setFeedback({ ...feedback, feedback: e.target.value })} placeholder="Strengths, concerns, red flags…" data-testid="interview-feedback-input" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { setFeedbackFor(null); setRawNotes(''); setAiSummary(null); }}>Cancel</Button>
                <Button onClick={submitFeedback} className="bg-emerald-600 hover:bg-emerald-700" data-testid="interview-feedback-submit"><Check className="h-4 w-4 mr-1" /> Save &amp; mark completed</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InterviewsPage;
